import { useQuery } from "@tanstack/react-query";
import { Pause, Play } from "lucide-react";

import { ContributorAvatar } from "@/components/contributor-avatar";
import { BackLink } from "@/components/explore/back-link";
import { TagList } from "@/components/explore/tag-list";
import { Image } from "@/components/image";
import { Link } from "@/components/link";
import { usePlayer } from "@/components/providers/player-provider";
import { dummyIcon } from "@/lib/consts";
import type { PublicParticipant } from "@/lib/explore";
import { contributorQuery, episodeQuery } from "@/lib/queries";
import { cn, formatDate } from "@/lib/utils";

/**
 * Credits, grouped by role.
 *
 * Roles are site-defined rather than a fixed set, so this groups whatever
 * comes back instead of hardcoding Host/Director/Techie — a new role appears
 * without a code change. Hosts lead because that is who listeners look for.
 */
const ROLE_ORDER = ["Host", "Director", "Techie"];

const groupByRole = (participants: PublicParticipant[]) => {
  const groups = new Map<string, PublicParticipant[]>();
  for (const participant of participants) {
    const members = groups.get(participant.roleName) ?? [];
    members.push(participant);
    groups.set(participant.roleName, members);
  }
  return [...groups.entries()].toSorted(([a], [b]) => {
    const rank = (role: string) => {
      const index = ROLE_ORDER.indexOf(role);
      return index === -1 ? ROLE_ORDER.length : index;
    };
    return rank(a) - rank(b) || a.localeCompare(b);
  });
};

function CreditedPerson({ participant }: { participant: PublicParticipant }) {
  const { data: person } = useQuery(
    contributorQuery(participant.contributorId)
  );

  return (
    <Link
      className="flex items-center gap-3 rounded-2xl p-2 transition-colors hover:bg-muted"
      href={`/view/people/${participant.contributorId}`}
    >
      <ContributorAvatar
        className="size-11 rounded-xl text-sm"
        fields={person?.fields ?? []}
        name={participant.firstName}
      />
      <span className="min-w-0 truncate font-medium">
        {participant.firstName}
      </span>
    </Link>
  );
}

export function EpisodePage({ episodeId }: { episodeId: string }) {
  const { data: episode, isPending } = useQuery(episodeQuery(episodeId));
  const { current, playRecording, playing, toggle } = usePlayer();

  if (isPending) {
    return <p className="py-16 text-center text-muted-foreground">Loading…</p>;
  }

  if (!episode) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">
          That episode could not be found.
        </p>
        <BackLink className="mt-4" />
      </div>
    );
  }

  const url = episode.recordingUrl;
  const isCurrent = current?.kind === "recording" && current.url === url;
  const isPlaying = isCurrent && playing;

  const handlePlay = () => {
    if (!url) {
      return;
    }
    if (isCurrent) {
      toggle();
      return;
    }
    playRecording({
      episodeId: episode.id,
      iconUrl: episode.iconUrl ?? dummyIcon,
      showName: episode.showName,
      title: episode.title,
      url,
    });
  };

  return (
    <div>
      <BackLink className="mb-6" />

      <header className="flex flex-col gap-6 sm:flex-row sm:items-end">
        <div className="relative aspect-square w-40 shrink-0 overflow-hidden rounded-2xl bg-muted shadow-sm sm:w-56">
          <Image
            fill
            src={episode.iconUrl ?? dummyIcon}
            alt={`${episode.title} art`}
            className="object-cover"
            priority
            sizes="(min-width: 640px) 224px, 160px"
          />
        </div>

        <div className="min-w-0 flex-1">
          <Link
            className="text-sm font-semibold tracking-wide text-primary uppercase hover:underline"
            href={`/view/shows/${episode.showId}`}
          >
            {episode.showName}
          </Link>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">
            {episode.title}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {formatDate(new Date(episode.startsAt))}
            {episode.cancelled && " · Cancelled"}
            {episode.special && " · Special"}
          </p>

          <div className="mt-3">
            <TagList tags={episode.tags} />
          </div>

          {url ? (
            <button
              className={cn(
                "mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5",
                "font-medium text-primary-foreground transition-colors hover:opacity-90"
              )}
              onClick={handlePlay}
              type="button"
            >
              {isPlaying ? (
                <Pause className="size-4 fill-current" />
              ) : (
                <Play className="size-4 fill-current" />
              )}
              {isPlaying ? "Pause" : "Listen"}
            </button>
          ) : (
            <p className="mt-5 text-sm text-muted-foreground">
              No recording available for this episode.
            </p>
          )}
        </div>
      </header>

      {episode.participants.length > 0 && (
        <section className="mt-12">
          <h2 className="text-xl font-semibold tracking-tight">Credits</h2>
          <div className="mt-4 flex flex-col gap-5">
            {groupByRole(episode.participants).map(([role, members]) => (
              <div key={role}>
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {role}
                </p>
                <ul className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {members.map((participant) => (
                    <li
                      key={`${participant.contributorId}:${participant.roleId}`}
                    >
                      <CreditedPerson participant={participant} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
